import React, {useEffect, useCallback, useMemo} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {useIntl} from 'react-intl'
import * as Yup from 'yup'
import {useFormik} from 'formik'

import {KTCard, KTCardBody, KTCardHeader, Loading, Datepicker, HelpIcon} from '../../../components'
import {PageTitle} from '../../../core'

import {useDispatch, useSelector} from 'react-redux'
import {
  bankPaymentOrderGetAvailablePayments,
  bankPaymentOrderCreate,
} from '../../../redux/slices/bankpaymentorder.slice'

import {
  useMemoizedPageUrl,
  useMemoizedValidationSchema,
  useMemoizedFormValues,
  useMemoizedBreadcrumbs
} from '../../../utils/performanceOptimizations'

const selectBankPaymentOrderData = (state) => ({
  isRedirect: state.bankpaymentorder.isRedirect,
  loading: state.bankpaymentorder.loading,
  getAvailablePayments: state.bankpaymentorder.getAvailablePayments
})

function Create() {
  const intl = useIntl()
  const navigate = useNavigate()
  const location = useLocation()
  
  const pageUrl = useMemoizedPageUrl(location)

  const {isRedirect, loading, getAvailablePayments} = useSelector(selectBankPaymentOrderData)
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(bankPaymentOrderGetAvailablePayments())
  }, [dispatch])

  const validSchema = useMemoizedValidationSchema(() => Yup.object().shape({
    Name: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    PaymentDate: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
    PaymentRequestList: Yup.array()
      .of(
        Yup.object().shape({
          PaymentRequestId: Yup.string().required(intl.formatMessage({id: 'REQUIRED.GENERAL'})),
        })
      )
      .min(1, intl.formatMessage({id: 'REQUIRED.GENERAL'})),
  }), [intl])

  const formValues = useMemoizedFormValues({
    Name: '',
    PaymentDate: new Date(),
    PaymentRequestList: [],
  })

  const handleSubmit = useCallback(async (values, {setSubmitting, resetForm}) => {
    setSubmitting(true)
    dispatch(bankPaymentOrderCreate(values))
    resetForm()
  }, [dispatch])

  const formik = useFormik({
    initialValues: formValues,
    validationSchema: validSchema,
    onSubmit: handleSubmit,
  })

  const handlePaymentDateChange = useCallback(([date]) => {
    formik.setFieldValue('PaymentDate', date)
  }, [formik])

  const handleCheckboxChange = useCallback((event, paymentRequestId) => {
    const isChecked = event.target.checked
    const newPaymentRequestList = isChecked
      ? [...formik.values.PaymentRequestList, {PaymentRequestId: paymentRequestId}]
      : formik.values.PaymentRequestList.filter(
          (item) => item.PaymentRequestId !== paymentRequestId
        )
    formik.setFieldValue('PaymentRequestList', newPaymentRequestList)
  }, [formik])

  const breadcrumbs = useMemoizedBreadcrumbs(() => [
    {
      title: intl.formatMessage({id: 'BC.CASH.BANKPAYMENTORDER'}),
      path: '/cash/bank-payment-order',
      isSeparator: false,
      isActive: false,
    },
  ], [intl])

  useEffect(() => {
    if (isRedirect && isRedirect.id) navigate(`/${pageUrl[1]}/${pageUrl[2]}/detail/${isRedirect.id}`)
  }, [isRedirect, navigate, pageUrl])

  const handleCancel = useCallback(() => {
    navigate(`/${pageUrl[1]}/${pageUrl[2]}`)
  }, [navigate, pageUrl])

  const tableHeaders = useMemo(() => [
    { key: 'PROJECTNAME', className: '' },
    { key: 'COMPANYTITLE', className: '' },
    { key: 'COMPANYBALANCE', className: '' },
    { key: 'REQUESTEDPAYMENT', className: '' },
    { key: 'REQUESTEDDATE', className: '' },
    { key: 'REQUESTEDUSERNAMESURNAME', className: '' },
    { key: 'APPROVEDPAYMENT', className: '' },
    { key: 'APPROVEDDATE', className: '' }
  ], [])

  return (
    <>
      <PageTitle breadcrumbs={breadcrumbs}>
        {intl.formatMessage({id: 'BC.CASH.BANKPAYMENTORDER.ADD'})}
      </PageTitle>
      <KTCard>
        <KTCardHeader title={intl.formatMessage({id: 'BC.CASH.BANKPAYMENTORDER.ADD'})}>
          <HelpIcon articleId='bank-payment-order-create' />
        </KTCardHeader>
        <KTCardBody>
          <form className='form' onSubmit={formik.handleSubmit} noValidate>
            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'BANKPAYMENTORDER.CREATE.NAME'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <input
                  type='text'
                  required
                  className='form-control form-control-solid mb-3 mb-lg-0'
                  autoComplete='off'
                  disabled={formik.isSubmitting}
                  {...formik.getFieldProps('Name')}
                />
                {formik.touched.Name && formik.errors.Name && (
                  <div className='fv-plugins-message-container'>
                    <div className='fv-help-block'>
                      <span role='alert'>{formik.errors.Name}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-3 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'BANKPAYMENTORDER.CREATE.PAYMENTDATE'})}
              </label>
              <div className='col-lg-6 fv-row fv-plugins-icon-container'>
                <Datepicker
                  {...formik.getFieldProps('PaymentDate')}
                  onChange={handlePaymentDateChange}
                />
              </div>
            </div>

            <div className='row mb-6'>
              <label className='col-lg-12 fw-bold fs-6 mb-2 required'>
                {intl.formatMessage({id: 'BANKPAYMENTORDER.CREATE.PAYMENTREQUESTLIST'})}
              </label>
              <div className='col-lg-12 fv-row fv-plugins-icon-container'>
                <div className='table-responsive mt-4'>
                  <table className='table align-middle gs-0 gy-4'>
                    <thead>
                      <tr className='fw-bold text-muted bg-light'>
                        <th className='w-25px'>&nbsp;</th>
                        {tableHeaders.map((header) => (
                          <th key={header.key} className={header.className}>
                            {intl.formatMessage({
                              id: `BANKPAYMENTORDER.CREATE.PAYMENTREQUESTLIST.${header.key}`,
                            })}
                          </th>
                        ))}
                      </tr>
                    </thead>

                    <tbody>
                      {getAvailablePayments && getAvailablePayments.map((item, index) => {
                        return (
                          <tr key={index}>
                            <td>
                              <div className='form-check form-check-sm form-check-custom form-check-solid'>
                                <input
                                  className='form-check-input widget-9-check'
                                  role='button'
                                  type='checkbox'
                                  value={item && item.paymentRequestId}
                                  onChange={(event) =>
                                    handleCheckboxChange(event, item && item.paymentRequestId)
                                  }
                                />
                              </div>
                            </td>
                            <td>
                              <div className='d-flex align-items-center'>
                                <div className='d-flex justify-content-start flex-column'>
                                  <div className='text-dark fw-bold fs-6'>{item && item.projectName}</div>
                                  <span className='text-muted fw-semibold text-muted d-block fs-7'>
                                    {item && item.name}
                                  </span>
                                </div>
                              </div>
                            </td>
                            <td className='text-muted fw-semibold'>{item && item.companyTitle}</td>
                            <td className='text-muted fw-semibold'>
                              {intl.formatNumber(item && item.companyBalance, {
                                style: 'currency',
                                currency: 'try',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 4,
                              })}
                            </td>
                            <td className='text-muted fw-semibold'>
                              {intl.formatNumber(item && item.requestedPayment, {
                                style: 'currency',
                                currency: 'try',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 4,
                              })}
                            </td>
                            <td className='text-muted fw-semibold'>
                              {intl.formatDate(new Date(item && item.requestedDate), {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </td>
                            <td className='text-muted fw-semibold'>
                              {item && item.requestedUserNameSurname}
                            </td>
                            <td className='text-muted fw-semibold'>
                              {intl.formatNumber(item && item.approvedPayment, {
                                style: 'currency',
                                currency: 'try',
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 4,
                              })}
                            </td>
                            <td className='text-muted fw-semibold'>
                              {intl.formatDate(new Date(item && item.approvedDate), {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                              })}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <div className='row pt-15'>
              <div className='col-lg-9 fv-row fv-plugins-icon-container d-flex gap-3 justify-content-center'>
                <button
                  className='btn btn-light'
                  onClick={handleCancel}
                >
                  {intl.formatMessage({id: 'BUTTON.CANCEL'})}
                </button>
                <button
                  type='submit'
                  className='btn btn-primary'
                  data-kt-users-modal-action='submit'
                  disabled={formik.isSubmitting || !formik.isValid || !formik.touched}
                >
                  <span className='indicator-label'>
                    {intl.formatMessage({id: 'BUTTON.SUBMIT'})}
                  </span>
                  {formik.isSubmitting && (
                    <span className='indicator-progress'>
                      {intl.formatMessage({id: 'PLEASE'})}
                      <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                    </span>
                  )}
                </button>
              </div>
            </div>
          </form>
        </KTCardBody>
      </KTCard>

      {loading && <Loading />}
    </>
  )
}
export {Create}
